/**
 * interactive.ts — Blocos INTERATIVOS oficiais do Studio (Fases 3 e 4 da
 * biblioteca própria)
 *
 * Módulo NEUTRO (sem DOM, React, Node ou Deno). Reúne, em um só lugar:
 *   1. o desenho (HTML) de cada bloco interativo, usado igualmente pelo editor,
 *      pela página publicada, pelo PDF e pelo e-mail;
 *   2. o CSS desses blocos, emitido pelo próprio módulo;
 *   3. o COMPORTAMENTO — um único arquivo de script escrito por nós.
 *
 * Regras de segurança que não se negociam:
 *   - o comportamento NUNCA vem do documento: o script é este texto fixo, sem
 *     `eval`, sem `new Function` e sem ler nada além dos atributos
 *     `data-uaizy-*` que o nosso próprio compilador emite;
 *   - o script só é incluído na página quando ela realmente usa um bloco
 *     interativo, e nunca em PDF, e-mail ou proposta impressa;
 *   - nada de conteúdo do usuário vira HTML sem passar por `esc()`.
 *
 * Substitutos por meio de saída: em PDF e e-mail a sanfona sai aberta, as abas
 * saem empilhadas e o carrossel vira grade. Nenhum conteúdo fica escondido.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - Componentes interativos adaptam superfície,
 *            contorno e contraste ao tema; menu fixo deixa de impor fundo claro.
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (biblioteca própria, Fases 3 e 4)
 */

import { resolveAssetUrl } from './stockCatalog.ts';

/** Meios de saída. */
export type StudioInteractiveMode = 'editor' | 'published' | 'thumbnail' | 'pdf' | 'email';

/** Propriedades cruas de um bloco. */
export type Props = Record<string, unknown>;

/** Versão do comportamento (sobe junto com o compilador). */
export const STUDIO_RUNTIME_VERSION = 'runtime-1.0.0';

// =============================================================================
// Apoio
// =============================================================================

/** Escapa texto para HTML. */
function esc(valor: unknown): string {
  return String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Texto simples de uma propriedade. */
function t(props: Props, chave: string, padrao = ''): string {
  const valor = props?.[chave];
  return valor == null || valor === '' ? padrao : String(valor);
}

/** Lista "uma por linha, partes separadas por |". */
function linhas(props: Props, chave: string): string[][] {
  return t(props, chave)
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => linha.split('|').map((parte) => parte.trim()));
}

/** Endereço de imagem já resolvido (aceita `acervo://`). */
function img(valor: string): string {
  return resolveAssetUrl(valor || '');
}

/** Verdadeiro quando o meio de saída é impressão ou e-mail. */
function estatico(modo: StudioInteractiveMode): boolean {
  return modo === 'pdf' || modo === 'email' || modo === 'thumbnail';
}

/** Cabeçalho opcional de seção. */
function cabecalho(props: Props): string {
  const titulo = t(props, 'title');
  const subtitulo = t(props, 'subtitle');
  if (!titulo && !subtitulo) return '';
  return `<div class="st-ib-head">${titulo ? `<h2>${esc(titulo)}</h2>` : ''}${
    subtitulo ? `<p class="page-lead">${esc(subtitulo)}</p>` : ''
  }</div>`;
}

// =============================================================================
// Desenho de cada bloco
// =============================================================================

type Desenho = (props: Props, modo: StudioInteractiveMode) => string;

/** Perguntas frequentes em sanfona (abre e fecha sem script). */
const desenhoAccordion: Desenho = (p, modo) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  const abrirTodos = estatico(modo);
  const abrirPrimeiro = t(p, 'openFirst', 'true') === 'true';
  const corpo = itens
    .map(([pergunta, resposta], i) => {
      const aberto = abrirTodos || (i === 0 && abrirPrimeiro);
      return `<details class="st-ib-acc-item"${aberto ? ' open' : ''}>
        <summary class="st-ib-acc-q">${esc(pergunta ?? '')}</summary>
        <div class="st-ib-acc-a">${esc(resposta ?? '')}</div>
      </details>`;
    })
    .join('');
  return `<section class="st-ib st-ib-acc">${cabecalho(p)}<div class="st-ib-acc-list">${corpo}</div></section>`;
};

/** Abas de conteúdo. */
const desenhoTabs: Desenho = (p, modo) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  // Impressão e e-mail: tudo empilhado, com o rótulo virando título.
  if (estatico(modo)) {
    const empilhado = itens
      .map(
        ([rotulo, texto]) =>
          `<div class="st-ib-tab-stack"><h3>${esc(rotulo ?? '')}</h3><p>${esc(texto ?? '')}</p></div>`,
      )
      .join('');
    return `<section class="st-ib">${cabecalho(p)}${empilhado}</section>`;
  }
  const botoes = itens
    .map(
      ([rotulo], i) =>
        `<button type="button" class="st-ib-tab${i === 0 ? ' is-on' : ''}" data-uaizy-tab="${i}">${esc(
          rotulo ?? '',
        )}</button>`,
    )
    .join('');
  const paineis = itens
    .map(
      ([, texto], i) =>
        `<div class="st-ib-tabpanel${i === 0 ? ' is-on' : ''}" data-uaizy-tabpanel="${i}"><p>${esc(
          texto ?? '',
        )}</p></div>`,
    )
    .join('');
  return `<section class="st-ib st-ib-tabs" data-uaizy-st="tabs">${cabecalho(
    p,
  )}<div class="st-ib-tablist">${botoes}</div><div class="st-ib-tabpanels">${paineis}</div></section>`;
};

/** Carrossel com rolagem por encaixe e setas. */
const desenhoCarousel: Desenho = (p, modo) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  const cartoes = itens
    .map(
      ([imagem, titulo, texto]) => `<article class="st-ib-slide">
        ${imagem ? `<img src="${esc(img(imagem))}" alt="${esc(titulo ?? '')}" loading="lazy" />` : ''}
        ${titulo ? `<h3>${esc(titulo)}</h3>` : ''}
        ${texto ? `<p>${esc(texto)}</p>` : ''}
      </article>`,
    )
    .join('');
  if (estatico(modo)) {
    return `<section class="st-ib">${cabecalho(p)}<div class="st-ib-slides-grid">${cartoes}</div></section>`;
  }
  return `<section class="st-ib st-ib-carousel" data-uaizy-st="carousel">${cabecalho(p)}
    <div class="st-ib-track" data-uaizy-track>${cartoes}</div>
    <div class="st-ib-arrows">
      <button type="button" class="st-ib-arrow" data-uaizy-prev aria-label="Anterior">&#8249;</button>
      <button type="button" class="st-ib-arrow" data-uaizy-next aria-label="Próximo">&#8250;</button>
    </div>
  </section>`;
};

/** Esteira contínua de logos (100% CSS). */
const desenhoMarquee: Desenho = (p, modo) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  const marcas = itens
    .map(([endereco, nome]) =>
      endereco?.startsWith('http') || endereco?.startsWith('acervo://') || endereco?.startsWith('data:')
        ? `<img src="${esc(img(endereco))}" alt="${esc(nome ?? '')}" loading="lazy" />`
        : `<span>${esc(endereco ?? '')}</span>`,
    )
    .join('');
  if (estatico(modo)) {
    return `<section class="st-ib"><div class="page-logos">${marcas}</div></section>`;
  }
  const velocidade = t(p, 'speed', 'normal');
  return `<section class="st-ib st-ib-marquee st-ib-marquee-${esc(velocidade)}">
    <div class="st-ib-marquee-row">${marcas}${marcas}</div>
  </section>`;
};

/** Comparador antes e depois. */
const desenhoCompare: Desenho = (p, modo) => {
  const antes = t(p, 'beforeUrl');
  const depois = t(p, 'afterUrl');
  if (!antes || !depois) return '';
  if (estatico(modo)) {
    return `<section class="st-ib"><div class="page-grid" style="grid-template-columns:1fr 1fr">
      <figure><img src="${esc(img(antes))}" alt="${esc(t(p, 'beforeLabel', 'Antes'))}" /><figcaption>${esc(
        t(p, 'beforeLabel', 'Antes'),
      )}</figcaption></figure>
      <figure><img src="${esc(img(depois))}" alt="${esc(t(p, 'afterLabel', 'Depois'))}" /><figcaption>${esc(
        t(p, 'afterLabel', 'Depois'),
      )}</figcaption></figure>
    </div></section>`;
  }
  return `<section class="st-ib st-ib-compare" data-uaizy-st="compare">${cabecalho(p)}
    <div class="st-ib-compare-box" data-uaizy-compare>
      <img class="st-ib-compare-a" src="${esc(img(antes))}" alt="${esc(t(p, 'beforeLabel', 'Antes'))}" />
      <div class="st-ib-compare-b" data-uaizy-compare-top>
        <img src="${esc(img(depois))}" alt="${esc(t(p, 'afterLabel', 'Depois'))}" />
      </div>
      <span class="st-ib-compare-line" data-uaizy-compare-line></span>
      <span class="st-ib-compare-tag st-ib-compare-tag-a">${esc(t(p, 'beforeLabel', 'Antes'))}</span>
      <span class="st-ib-compare-tag st-ib-compare-tag-b">${esc(t(p, 'afterLabel', 'Depois'))}</span>
    </div>
  </section>`;
};

/** Planos com chave mensal/anual. */
const desenhoPlans: Desenho = (p, modo) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  const rotuloMes = t(p, 'monthlyLabel', 'Mensal');
  const rotuloAno = t(p, 'annualLabel', 'Anual');
  const cartoes = itens
    .map(([nome, mensal, anual, recursos, destaque, botao, destino]) => {
      const lista = (recursos ?? '')
        .split(';')
        .map((r) => r.trim())
        .filter(Boolean)
        .map((r) => `<li>${esc(r)}</li>`)
        .join('');
      return `<article class="page-card page-plan${destaque === 'sim' ? ' page-plan-featured' : ''}">
        <h3>${esc(nome ?? '')}</h3>
        <p class="page-price"><span data-uaizy-price-m>${esc(mensal ?? '')}</span><span data-uaizy-price-y hidden>${esc(
          anual ?? mensal ?? '',
        )}</span></p>
        ${lista ? `<ul class="page-plan-list">${lista}</ul>` : ''}
        ${
          botao
            ? `<a class="page-cta" href="${esc(destino || '#formulario')}">${esc(botao)}</a>`
            : ''
        }
      </article>`;
    })
    .join('');
  const grade = `<div class="page-grid st-ib-plans-grid">${cartoes}</div>`;
  if (estatico(modo)) return `<section class="st-ib">${cabecalho(p)}${grade}</section>`;
  return `<section class="st-ib st-ib-plans" data-uaizy-st="plans">${cabecalho(p)}
    <div class="st-ib-switch">
      <button type="button" class="is-on" data-uaizy-cycle="m">${esc(rotuloMes)}</button>
      <button type="button" data-uaizy-cycle="y">${esc(rotuloAno)}</button>
    </div>
    ${grade}
  </section>`;
};

/** Linha do tempo (100% CSS). */
const desenhoTimeline: Desenho = (p) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  const passos = itens
    .map(
      ([marco, titulo, texto]) => `<li class="st-ib-tl-item">
        <span class="st-ib-tl-dot"></span>
        <div><span class="st-ib-tl-when">${esc(marco ?? '')}</span>
        <h3>${esc(titulo ?? '')}</h3>${texto ? `<p>${esc(texto)}</p>` : ''}</div>
      </li>`,
    )
    .join('');
  return `<section class="st-ib st-ib-tl">${cabecalho(p)}<ul class="st-ib-tl-list">${passos}</ul></section>`;
};

/** Indicadores com contagem crescente. */
const desenhoMetrics: Desenho = (p, modo) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  const cartoes = itens
    .map(
      ([valor, rotulo, sufixo]) => `<div class="st-ib-metric">
        <p class="page-stat-value"><span${
          estatico(modo) ? '' : ` data-uaizy-count="${esc(valor ?? '')}"`
        }>${esc(valor ?? '')}</span>${sufixo ? `<span>${esc(sufixo)}</span>` : ''}</p>
        <p class="page-stat-label">${esc(rotulo ?? '')}</p>
      </div>`,
    )
    .join('');
  return `<section class="st-ib st-ib-metrics"${
    estatico(modo) ? '' : ' data-uaizy-st="metrics"'
  }>${cabecalho(p)}<div class="st-ib-metrics-row">${cartoes}</div></section>`;
};

/** Galeria com ampliação da foto. */
const desenhoLightbox: Desenho = (p, modo) => {
  const itens = linhas(p, 'items');
  if (!itens.length) return '';
  const colunas = t(p, 'columns', '3');
  const fotos = itens
    .map(
      ([endereco, legenda]) =>
        `<figure class="st-ib-lb-item"${
          estatico(modo) ? '' : ` data-uaizy-zoom="${esc(img(endereco ?? ''))}"`
        }>
          <img src="${esc(img(endereco ?? ''))}" alt="${esc(legenda ?? '')}" loading="lazy" />
          ${legenda ? `<figcaption>${esc(legenda)}</figcaption>` : ''}
        </figure>`,
    )
    .join('');
  return `<section class="st-ib st-ib-lb"${
    estatico(modo) ? '' : ' data-uaizy-st="lightbox"'
  }>${cabecalho(p)}<div class="page-grid" style="grid-template-columns:repeat(${esc(
    colunas,
  )},minmax(0,1fr))">${fotos}</div></section>`;
};

/** Botão flutuante de contato. */
const desenhoFloating: Desenho = (p, modo) => {
  if (estatico(modo)) return '';
  const rotulo = t(p, 'label', 'Fale conosco');
  const destino = t(p, 'href', '#formulario');
  const lado = t(p, 'side', 'right') === 'left' ? 'left' : 'right';
  return `<a class="st-ib-float st-ib-float-${lado}" href="${esc(destino)}">${esc(rotulo)}</a>`;
};

/** Barra de aviso dispensável. */
const desenhoNotice: Desenho = (p, modo) => {
  const texto = t(p, 'text');
  if (!texto) return '';
  const destino = t(p, 'href');
  const rotulo = t(p, 'linkLabel');
  const miolo = `${esc(texto)}${
    destino && rotulo ? ` <a href="${esc(destino)}">${esc(rotulo)}</a>` : ''
  }`;
  if (estatico(modo)) return `<div class="page-banner">${miolo}</div>`;
  return `<div class="page-banner st-ib-notice" data-uaizy-st="notice">${miolo}
    <button type="button" class="st-ib-notice-x" data-uaizy-dismiss aria-label="Fechar aviso">&times;</button>
  </div>`;
};

/** Menu fixo com destaque da seção atual e menu de celular. */
const desenhoStickyNav: Desenho = (p, modo) => {
  const marca = t(p, 'brand', 'Sua marca');
  const logo = t(p, 'logoUrl');
  const itens = linhas(p, 'items');
  const links = itens
    .map(([rotulo, destino]) => `<a href="${esc(destino ?? '#')}">${esc(rotulo ?? '')}</a>`)
    .join('');
  const cta = t(p, 'ctaLabel');
  const ctaHref = t(p, 'ctaHref', '#formulario');
  const bloco = `<div class="page-nav-brand">${
    logo ? `<img src="${esc(img(logo))}" alt="${esc(marca)}" style="max-height:28px" />` : ''
  }<span>${esc(marca)}</span></div>
    <nav class="page-nav-links" data-uaizy-navlinks>${links}</nav>
    ${cta ? `<a class="page-nav-cta" href="${esc(ctaHref)}">${esc(cta)}</a>` : ''}`;
  if (estatico(modo)) return `<div class="page-nav">${bloco}</div>`;
  return `<div class="page-nav st-ib-nav" data-uaizy-st="nav">
    ${bloco}
    <button type="button" class="st-ib-nav-toggle" data-uaizy-navtoggle aria-label="Abrir menu">&#9776;</button>
  </div>`;
};

/** Registro de desenhos por chave de bloco. */
export const STUDIO_INTERACTIVE_BLOCKS: Record<string, Desenho> = {
  'page.accordion': desenhoAccordion,
  'page.tabs': desenhoTabs,
  'page.carousel': desenhoCarousel,
  'page.marquee': desenhoMarquee,
  'page.compare': desenhoCompare,
  'page.plans': desenhoPlans,
  'page.timeline': desenhoTimeline,
  'page.metrics': desenhoMetrics,
  'page.lightbox': desenhoLightbox,
  'page.floatingCta': desenhoFloating,
  'page.notice': desenhoNotice,
  'page.stickyNav': desenhoStickyNav,
};

/** Desenha um bloco interativo (string vazia quando a chave não existe). */
export function renderStudioInteractiveBlock(
  tipo: string,
  props: Props,
  modo: StudioInteractiveMode = 'published',
): string {
  const desenho = STUDIO_INTERACTIVE_BLOCKS[tipo];
  return desenho ? desenho(props ?? {}, modo) : '';
}

/** Chaves atendidas por este módulo. */
export function listStudioInteractiveBlocks(): string[] {
  return Object.keys(STUDIO_INTERACTIVE_BLOCKS);
}

// =============================================================================
// CSS dos blocos interativos
// =============================================================================

export function interactiveStyles(): string {
  return `      /* Blocos interativos oficiais (biblioteca própria) */
      .st-ib { padding: 40px 0; }
      .st-ib-head { text-align: center; margin-bottom: 26px; }
      .st-ib-head h2 { font-size: clamp(22px, 3.2cqi, 32px); margin: 0 0 8px 0; }

      /* Sanfona */
      .st-ib-acc-list { display: grid; gap: 10px; max-width: 820px; margin: 0 auto; }
      .st-ib-acc-item { border: 1px solid color-mix(in srgb, currentColor 12%, transparent); border-radius: 14px; background: color-mix(in srgb, currentColor 4%, transparent); color: inherit; padding: 4px 18px; }
      .st-ib-acc-q { cursor: pointer; font-weight: 600; padding: 14px 0; list-style: none; }
      .st-ib-acc-q::-webkit-details-marker { display: none; }
      .st-ib-acc-q::after { content: "+"; float: right; font-weight: 400; opacity: .6; }
      .st-ib-acc-item[open] .st-ib-acc-q::after { content: "\\2212"; }
      .st-ib-acc-a { padding: 0 0 16px 0; color: inherit; opacity: .72; font-size: 14.5px; }

      /* Abas */
      .st-ib-tablist { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 18px; }
      .st-ib-tab { border: 1px solid color-mix(in srgb, currentColor 14%, transparent); background: color-mix(in srgb, currentColor 4%, transparent); color: inherit; border-radius: 999px; padding: 8px 18px; font-size: 14px; cursor: pointer; }
      .st-ib-tab.is-on { background: var(--st-accent, #111827); color: #fff; border-color: var(--st-accent, #111827); }
      .st-ib-tabpanel { display: none; max-width: 780px; margin: 0 auto; text-align: center; color: inherit; opacity: .82; }
      .st-ib-tabpanel.is-on { display: block; }
      .st-ib-tab-stack { margin-bottom: 16px; }

      /* Carrossel */
      .st-ib-track { display: flex; gap: 18px; overflow-x: auto; scroll-snap-type: x mandatory; scroll-behavior: smooth; padding-bottom: 8px; }
      .st-ib-track::-webkit-scrollbar { height: 6px; }
      .st-ib-slide { flex: 0 0 min(340px, 82%); scroll-snap-align: center; border: 1px solid color-mix(in srgb, currentColor 12%, transparent); border-radius: 16px; padding: 18px; background: color-mix(in srgb, currentColor 4%, transparent); color: inherit; }
      .st-ib-slide img { width: 100%; border-radius: 12px; margin-bottom: 12px; object-fit: cover; aspect-ratio: 16/10; }
      .st-ib-slides-grid { display: grid; gap: 18px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
      .st-ib-arrows { display: flex; gap: 8px; justify-content: center; margin-top: 16px; }
      .st-ib-arrow { width: 40px; height: 40px; border-radius: 999px; border: 1px solid color-mix(in srgb, currentColor 14%, transparent); background: color-mix(in srgb, currentColor 5%, transparent); color: inherit; font-size: 20px; line-height: 1; cursor: pointer; }

      /* Esteira de logos */
      @keyframes st-ib-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      .st-ib-marquee { overflow: hidden; -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent); }
      .st-ib-marquee-row { display: flex; align-items: center; gap: 48px; width: max-content; animation: st-ib-marquee 32s linear infinite; }
      .st-ib-marquee-slow .st-ib-marquee-row { animation-duration: 52s; }
      .st-ib-marquee-fast .st-ib-marquee-row { animation-duration: 18s; }
      .st-ib-marquee img { max-height: 34px; opacity: .75; }

      /* Antes e depois */
      .st-ib-compare-box { position: relative; max-width: 900px; margin: 0 auto; border-radius: 18px; overflow: hidden; user-select: none; touch-action: none; }
      .st-ib-compare-box img { display: block; width: 100%; }
      .st-ib-compare-b { position: absolute; inset: 0; width: 50%; overflow: hidden; }
      .st-ib-compare-b img { width: 100vw; max-width: none; height: 100%; object-fit: cover; }
      .st-ib-compare-line { position: absolute; top: 0; bottom: 0; left: 50%; width: 3px; background: #fff; box-shadow: 0 0 0 1px rgba(0,0,0,.15); cursor: ew-resize; }
      .st-ib-compare-tag { position: absolute; bottom: 14px; padding: 5px 12px; border-radius: 999px; background: rgba(17,24,39,.72); color: #fff; font-size: 12px; }
      .st-ib-compare-tag-a { left: 14px; }
      .st-ib-compare-tag-b { right: 14px; }

      /* Planos com chave mensal/anual */
      .st-ib-switch { display: inline-flex; gap: 4px; padding: 4px; border: 1px solid color-mix(in srgb, currentColor 14%, transparent); border-radius: 999px; margin: 0 auto 22px auto; background: color-mix(in srgb, currentColor 4%, transparent); }
      .st-ib-plans { text-align: center; }
      .st-ib-switch button { border: none; background: transparent; color: inherit; border-radius: 999px; padding: 8px 18px; font-size: 13.5px; cursor: pointer; }
      .st-ib-switch button.is-on { background: var(--st-accent, #111827); color: #fff; }
      .st-ib-plans-grid { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); text-align: left; }

      /* Linha do tempo */
      .st-ib-tl-list { list-style: none; margin: 0 auto; padding: 0; max-width: 760px; position: relative; }
      .st-ib-tl-list::before { content: ""; position: absolute; left: 9px; top: 6px; bottom: 6px; width: 2px; background: color-mix(in srgb, currentColor 14%, transparent); }
      .st-ib-tl-item { position: relative; padding: 0 0 26px 38px; }
      .st-ib-tl-dot { position: absolute; left: 2px; top: 4px; width: 16px; height: 16px; border-radius: 999px; background: var(--st-accent, #2563eb); box-shadow: 0 0 0 4px rgba(37,99,235,.14); }
      .st-ib-tl-when { font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: inherit; opacity: .68; }
      .st-ib-tl-item h3 { margin: 4px 0 4px 0; font-size: 17px; }
      .st-ib-tl-item p { margin: 0; color: inherit; opacity: .76; font-size: 14.5px; }

      /* Indicadores */
      .st-ib-metrics-row { display: grid; gap: 18px; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); text-align: center; }

      /* Galeria com ampliação */
      .st-ib-lb-item { margin: 0; cursor: zoom-in; }
      .st-ib-lb-item img { width: 100%; border-radius: 14px; object-fit: cover; aspect-ratio: 4/3; }
      .st-ib-lb-item figcaption { font-size: 13px; color: inherit; opacity: .72; margin-top: 6px; }
      .st-lb-overlay { position: fixed; inset: 0; background: rgba(3,7,18,.88); display: flex; align-items: center; justify-content: center; z-index: 9999; padding: 24px; cursor: zoom-out; }
      .st-lb-overlay img { max-width: 100%; max-height: 100%; border-radius: 12px; }

      /* Botão flutuante */
      .st-ib-float { position: fixed; bottom: 22px; z-index: 60; padding: 13px 22px; border-radius: 999px; background: #111827; color: #fff !important; font-weight: 600; font-size: 14px; text-decoration: none; box-shadow: 0 12px 30px rgba(0,0,0,.22); }
      .st-ib-float-right { right: 22px; }
      .st-ib-float-left { left: 22px; }

      /* Barra de aviso */
      .st-ib-notice { position: relative; padding-right: 44px; }
      .st-ib-notice-x { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); border: none; background: transparent; font-size: 18px; line-height: 1; cursor: pointer; color: inherit; }

      /* Menu fixo */
      .st-ib-nav { position: sticky; top: 0; z-index: 50; backdrop-filter: blur(14px); background: color-mix(in srgb, currentColor 7%, transparent); border-bottom: 1px solid color-mix(in srgb, currentColor 12%, transparent); color: inherit; }
      .st-ib-nav.is-scrolled { box-shadow: 0 8px 24px rgba(0,0,0,.08); }
      .st-ib-nav .page-nav-links a.is-current { opacity: 1; font-weight: 600; }
      .st-ib-nav-toggle { display: none; border: 1px solid color-mix(in srgb, currentColor 14%, transparent); background: color-mix(in srgb, currentColor 5%, transparent); color: inherit; border-radius: 10px; width: 38px; height: 38px; font-size: 17px; cursor: pointer; }
      @container doc (max-width: 720px) {
        .st-ib-nav-toggle { display: inline-block; }
        .st-ib-nav .page-nav-links { display: none; width: 100%; flex-direction: column; align-items: flex-start; gap: 10px; }
        .st-ib-nav .page-nav-links.is-open { display: flex; }
        .st-ib-compare-tag { font-size: 11px; }
      }
      @media print {
        .st-ib-float, .st-ib-notice-x, .st-ib-arrows, .st-ib-nav-toggle { display: none !important; }
        .st-ib-tabpanel { display: block !important; }
      }
      @media (prefers-reduced-motion: reduce) {
        .st-ib-marquee-row { animation: none; }
        .st-ib-track { scroll-behavior: auto; }
      }`;
}

// =============================================================================
// Comportamento — arquivo único, escrito por nós
// =============================================================================

/**
 * Script do Studio.
 * Só lê atributos `data-uaizy-*` emitidos pelo compilador. Não usa `eval`, não
 * lê conteúdo do documento como código e não toca em cookies nem em
 * armazenamento do navegador.
 */
export const STUDIO_RUNTIME_JS = `(function(){
  "use strict";
  var d = document;
  function all(sel, raiz){ return Array.prototype.slice.call((raiz||d).querySelectorAll(sel)); }

  /* Abas */
  all('[data-uaizy-st="tabs"]').forEach(function(bloco){
    var botoes = all('[data-uaizy-tab]', bloco);
    var paineis = all('[data-uaizy-tabpanel]', bloco);
    botoes.forEach(function(botao){
      botao.addEventListener('click', function(){
        var alvo = botao.getAttribute('data-uaizy-tab');
        botoes.forEach(function(b){ b.classList.toggle('is-on', b === botao); });
        paineis.forEach(function(p){ p.classList.toggle('is-on', p.getAttribute('data-uaizy-tabpanel') === alvo); });
      });
    });
  });

  /* Carrossel */
  all('[data-uaizy-st="carousel"]').forEach(function(bloco){
    var trilho = bloco.querySelector('[data-uaizy-track]');
    if (!trilho) return;
    function passo(){ var item = trilho.firstElementChild; return item ? item.getBoundingClientRect().width + 18 : 320; }
    var anterior = bloco.querySelector('[data-uaizy-prev]');
    var proximo = bloco.querySelector('[data-uaizy-next]');
    if (anterior) anterior.addEventListener('click', function(){ trilho.scrollBy({ left: -passo(), behavior: 'smooth' }); });
    if (proximo) proximo.addEventListener('click', function(){ trilho.scrollBy({ left: passo(), behavior: 'smooth' }); });
  });

  /* Antes e depois */
  all('[data-uaizy-st="compare"]').forEach(function(bloco){
    var caixa = bloco.querySelector('[data-uaizy-compare]');
    var topo = bloco.querySelector('[data-uaizy-compare-top]');
    var linha = bloco.querySelector('[data-uaizy-compare-line]');
    if (!caixa || !topo || !linha) return;
    var arrastando = false;
    function mover(x){
      var r = caixa.getBoundingClientRect();
      var pct = Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100));
      topo.style.width = pct + '%';
      linha.style.left = pct + '%';
    }
    caixa.addEventListener('pointerdown', function(e){ arrastando = true; mover(e.clientX); });
    d.addEventListener('pointermove', function(e){ if (arrastando) mover(e.clientX); });
    d.addEventListener('pointerup', function(){ arrastando = false; });
  });

  /* Planos: chave mensal/anual */
  all('[data-uaizy-st="plans"]').forEach(function(bloco){
    var botoes = all('[data-uaizy-cycle]', bloco);
    botoes.forEach(function(botao){
      botao.addEventListener('click', function(){
        var ciclo = botao.getAttribute('data-uaizy-cycle');
        botoes.forEach(function(b){ b.classList.toggle('is-on', b === botao); });
        all('[data-uaizy-price-m]', bloco).forEach(function(el){ el.hidden = ciclo !== 'm'; });
        all('[data-uaizy-price-y]', bloco).forEach(function(el){ el.hidden = ciclo !== 'y'; });
      });
    });
  });

  /* Indicadores que sobem ao aparecer na tela */
  var contadores = all('[data-uaizy-count]');
  if (contadores.length && 'IntersectionObserver' in window) {
    var observador = new IntersectionObserver(function(entradas){
      entradas.forEach(function(entrada){
        if (!entrada.isIntersecting) return;
        var el = entrada.target;
        observador.unobserve(el);
        var bruto = el.getAttribute('data-uaizy-count') || '';
        var numero = parseFloat(bruto.replace(/[^0-9.,-]/g, '').replace(/\\./g, '').replace(',', '.'));
        if (!isFinite(numero)) return;
        var prefixo = bruto.split(/[0-9]/)[0] || '';
        var casas = (bruto.indexOf(',') > -1) ? 1 : 0;
        var inicio = null;
        function quadro(agora){
          if (inicio === null) inicio = agora;
          var p = Math.min(1, (agora - inicio) / 1200);
          var valor = numero * (1 - Math.pow(1 - p, 3));
          el.textContent = prefixo + valor.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
          if (p < 1) requestAnimationFrame(quadro); else el.textContent = bruto;
        }
        requestAnimationFrame(quadro);
      });
    }, { threshold: 0.4 });
    contadores.forEach(function(el){ observador.observe(el); });
  }

  /* Galeria com ampliação */
  all('[data-uaizy-zoom]').forEach(function(item){
    item.addEventListener('click', function(){
      var src = item.getAttribute('data-uaizy-zoom');
      if (!src) return;
      var capa = d.createElement('div');
      capa.className = 'st-lb-overlay';
      var foto = d.createElement('img');
      foto.src = src;
      foto.alt = '';
      capa.appendChild(foto);
      capa.addEventListener('click', function(){ capa.remove(); });
      d.addEventListener('keydown', function fechar(e){
        if (e.key === 'Escape') { capa.remove(); d.removeEventListener('keydown', fechar); }
      });
      d.body.appendChild(capa);
    });
  });

  /* Barra de aviso dispensável */
  all('[data-uaizy-dismiss]').forEach(function(botao){
    botao.addEventListener('click', function(){
      var barra = botao.closest('.st-ib-notice');
      if (barra) barra.remove();
    });
  });

  /* Menu fixo: sombra ao rolar, destaque da seção e menu de celular */
  all('[data-uaizy-st="nav"]').forEach(function(barra){
    var alternador = barra.querySelector('[data-uaizy-navtoggle]');
    var links = barra.querySelector('[data-uaizy-navlinks]');
    if (alternador && links) {
      alternador.addEventListener('click', function(){ links.classList.toggle('is-open'); });
    }
    window.addEventListener('scroll', function(){
      barra.classList.toggle('is-scrolled', window.scrollY > 8);
    }, { passive: true });
    var ancoras = all('a[href^="#"]', barra);
    var alvos = ancoras.map(function(a){
      var id = a.getAttribute('href').slice(1);
      return id ? d.getElementById(id) : null;
    });
    if ('IntersectionObserver' in window) {
      var visor = new IntersectionObserver(function(entradas){
        entradas.forEach(function(entrada){
          if (!entrada.isIntersecting) return;
          var indice = alvos.indexOf(entrada.target);
          ancoras.forEach(function(a, i){ a.classList.toggle('is-current', i === indice); });
        });
      }, { rootMargin: '-45% 0px -50% 0px' });
      alvos.forEach(function(alvo){ if (alvo) visor.observe(alvo); });
    }
  });

  /* Rolagem suave para âncoras internas */
  d.documentElement.style.scrollBehavior = 'smooth';
})();`;

/** Diz se o HTML compilado usa algum bloco interativo (define se o script entra). */
export function usaInteracao(html: string): boolean {
  return /data-uaizy-(st|zoom|count|dismiss)=/.test(String(html ?? ''));
}
